// ─── shadcn-style Primitives for Wireframes ─────────────────────────────────

export const cn = (...classes) => classes.filter(Boolean).join(" ");

export const Badge = ({ variant = "default", children, className }) => {
  const base = "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors border";
  const variants = {
    default: "bg-slate-100 text-slate-700 border-slate-200",
    destructive: "bg-red-50 text-red-700 border-red-200",
    critical: "bg-red-50 text-red-700 border-red-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    healthy: "bg-emerald-50 text-emerald-700 border-emerald-200",
    overstock: "bg-violet-50 text-violet-700 border-violet-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    draft: "bg-slate-100 text-slate-500 border-slate-200",
    urgent: "bg-red-50 text-red-700 border-red-200",
    high: "bg-orange-50 text-orange-700 border-orange-200",
    medium: "bg-amber-50 text-amber-700 border-amber-200",
    standard: "bg-slate-100 text-slate-500 border-slate-200",
    proposed: "bg-blue-50 text-blue-700 border-blue-200",
    simulating: "bg-violet-50 text-violet-700 border-violet-200",
    feasible: "bg-emerald-50 text-emerald-700 border-emerald-200",
    partial: "bg-amber-50 text-amber-700 border-amber-200",
    outline: "bg-transparent text-slate-700 border-slate-300",
  };
  return <span className={cn(base, variants[variant] || variants.default, className)}>{children}</span>;
};

export const Card = ({ children, className, style }) => (
  <div className={cn("rounded-xl border border-slate-200 bg-white shadow-sm", className)} style={style}>{children}</div>
);
export const CardHeader = ({ children, className }) => <div className={cn("p-5 pb-2", className)}>{children}</div>;
export const CardTitle = ({ children, className }) => <h3 className={cn("text-sm font-semibold text-slate-900 tracking-tight", className)}>{children}</h3>;
export const CardDescription = ({ children, className }) => <p className={cn("text-xs text-slate-500 mt-0.5", className)}>{children}</p>;
export const CardContent = ({ children, className }) => <div className={cn("p-5 pt-3", className)}>{children}</div>;

export const Button = ({ children, variant = "default", size = "default", className, onClick }) => {
  const base = "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 cursor-pointer";
  const vars = {
    default: "bg-slate-900 text-white hover:bg-slate-800 shadow-sm",
    secondary: "bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200",
    outline: "border border-slate-200 bg-white hover:bg-slate-50 text-slate-700",
    ghost: "hover:bg-slate-100 text-slate-600",
    destructive: "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200",
    success: "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm",
    accent: "bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200",
    violet: "bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200",
    amber: "bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200",
    link: "text-blue-600 underline-offset-4 hover:underline p-0 h-auto",
  };
  const sizes = {
    default: "h-9 px-4 py-2",
    sm: "h-8 rounded-md px-3 text-xs",
    lg: "h-10 rounded-lg px-6",
    icon: "h-9 w-9",
  };
  return <button onClick={onClick} className={cn(base, vars[variant], sizes[size], className)}>{children}</button>;
};

export const Progress = ({ value, max = 100, className, color }) => (
  <div className={cn("h-1.5 w-full overflow-hidden rounded-full bg-slate-100", className)}>
    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min((value / max) * 100, 100)}%`, backgroundColor: color || "#3b82f6" }} />
  </div>
);

export const Separator = ({ className }) => <div className={cn("h-px bg-slate-100", className)} />;

export const Spark = ({ data, color = "#3b82f6", w = 72, h = 22 }) => {
  const mx = Math.max(...data), mn = Math.min(...data), r = mx - mn || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - mn) / r) * h}`).join(" ");
  return <svg width={w} height={h}><polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
};

export const AIReasoning = ({ text, open, toggle }) => (
  <div className="mt-2">
    <button onClick={(e) => { e.stopPropagation(); toggle(); }} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium bg-transparent border-none cursor-pointer p-0">
      <span className="text-xs">◈</span> AI Reasoning {open ? "▾" : "▸"}
    </button>
    {open && (
      <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-slate-600 leading-relaxed">
        {text}
      </div>
    )}
  </div>
);

export const KPI = ({ label, value, delta, dir, sub, sm }) => (
  <Card className={sm ? "p-3" : "p-5"} style={{ flex: 1, minWidth: sm ? 130 : 170 }}>
    <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1.5">{label}</div>
    <div className={cn("font-bold text-slate-900", sm ? "text-xl" : "text-2xl")}>{value}</div>
    {delta && <div className={cn("text-xs font-medium mt-1", dir === "up" ? "text-emerald-600" : dir === "down" ? "text-red-600" : "text-slate-400")}>
      {dir === "up" ? "↑" : dir === "down" ? "" : ""} {delta}
    </div>}
    {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
  </Card>
);
